import React from 'react';
import { InlineLoadingState } from '../../components/LoadingSpinner';
import { UI_Z_CLASS } from '../../utils/uiZIndex';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../settings/utils/settingsSectionStyles';
import { AiShareFields, useAiShareMembers, type AiVisibility } from './AiShareFields';

type Draft = { name: string; description: string; instructions: string };

export function SkillDraftDialog({
  chatId,
  canEnable,
  onClose,
  onSaved,
}: {
  chatId: string;
  canEnable: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<Draft>({ name: '', description: '', instructions: '' });
  const [visibility, setVisibility] = React.useState<AiVisibility>('private');
  const [granteeUserIds, setGranteeUserIds] = React.useState<string[]>([]);
  const [enabled, setEnabled] = React.useState(false);
  const members = useAiShareMembers();

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/skill-draft`, { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        const body = await res.json().catch(() => ({})) as { error?: string; draft?: Draft };
        if (!res.ok) throw new Error(body.error || 'Skill-Vorschlag konnte nicht erzeugt werden.');
        if (!cancelled && body.draft) setDraft(body.draft);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Skill-Vorschlag konnte nicht erzeugt werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [chatId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/ai/skills', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          visibility,
          granteeUserIds: visibility === 'members' ? granteeUserIds : [],
          enabled: canEnable && enabled,
        }),
      });
      const body = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) throw new Error(body.error || 'Skill konnte nicht gespeichert werden.');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Skill konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${UI_Z_CLASS.fullscreenModal} flex items-center justify-center bg-black/40 p-4`}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-app-border bg-app-surface p-5" role="dialog" aria-labelledby="skill-draft-title">
        <h2 id="skill-draft-title" className="text-base font-bold text-app-text">Als Skill speichern</h2>
        {loading ? (
          <div className="py-8">
            <InlineLoadingState label="Skill wird vorgeschlagen …" />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className={SETTINGS_FIELD_LABEL}>Name</span>
              <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </label>
            <label className="block">
              <span className={SETTINGS_FIELD_LABEL}>Beschreibung</span>
              <input className={`${SETTINGS_INPUT_CLASS} mt-1.5 w-full`} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            </label>
            <label className="block">
              <span className={SETTINGS_FIELD_LABEL}>Anweisung</span>
              <textarea className={`${SETTINGS_TEXTAREA_CLASS} mt-1.5 w-full`} rows={8} value={draft.instructions} onChange={(event) => setDraft({ ...draft, instructions: event.target.value })} />
            </label>
            <AiShareFields
              idPrefix="skill-draft"
              visibility={visibility}
              onVisibilityChange={setVisibility}
              granteeUserIds={granteeUserIds}
              onGranteesChange={setGranteeUserIds}
              members={members}
            />
            {canEnable ? (
              <label className="flex items-center gap-2 text-sm text-app-text">
                <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
                Für den Assistenten aktivieren
              </label>
            ) : null}
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={onClose}>Abbrechen</button>
              <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving || !draft.name.trim()} onClick={() => void save()}>
                {saving ? 'Speichert …' : 'Speichern'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
